import React, {
  useEffect,
  useState,
} from "react";

import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Pagination,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";

import {
  ChevronDown,
  ChevronUp,
  ChevronsUp,
  Download,
  RefreshCcw,
  Search,
} from "react-feather";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import { toast } from "react-toastify";

import {
  clearPanchayathSubscriptionReportError,
  exportPanchayathSubscriptionReportThunk,
  fetchPanchayathSubscriptionReportThunk,
  REPORT_TYPES,
} from "../../../store/slices/panchayathSubscriptionReportsSlice";

import "./ReportPage.css";

const money = (value) => {
  const n = Number(value || 0);
  return `₹${Math.round(
    Number.isFinite(n) ? n : 0
  ).toLocaleString("en-IN")}`;
};

// Expected / Due are null for non-monthly subscriptions.
const moneyOrDash = (value) =>
  value === null || value === undefined || value === ""
    ? "—"
    : money(value);

const dueText = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value || 0);
  return (
    <span style={{ fontWeight: 700, color: n > 0 ? "#b42318" : "#067647" }}>
      {n > 0 ? money(n) : "Nil"}
    </span>
  );
};

const pendingText = (value) => {
  const n = Number(value || 0);
  return n > 0 ? (
    <span style={{ fontWeight: 600, color: "#b54708" }}>{money(n)}</span>
  ) : (
    "—"
  );
};

const dateText = (value) => {
  if (!value) return "—";

  const date = new Date(
    `${String(value).slice(0, 10)}T00:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const safe = (value, fallback = "—") =>
  value === null ||
  value === undefined ||
  value === ""
    ? fallback
    : value;

const downloadBlob = (
  blob,
  fileName
) => {
  const url =
    window.URL.createObjectURL(
      blob
    );

  const anchor =
    document.createElement(
      "a"
    );

  anchor.href = url;
  anchor.download =
    fileName;

  document.body.appendChild(
    anchor
  );

  anchor.click();
  anchor.remove();

  window.URL.revokeObjectURL(
    url
  );
};

const getCurrentFy = () => {
  const now = new Date();

  return now.getMonth() >= 3
    ? now.getFullYear()
    : now.getFullYear() - 1;
};

const ReportPagination = ({
  loading,
  page,
  totalPages,
  setPage,
}) => {
  if (totalPages <= 1) {
    return null;
  }

  const items = [];
  const maxButtons = 5;

  let start = Math.max(
    1,
    page -
      Math.floor(
        maxButtons / 2
      )
  );

  let end = Math.min(
    totalPages,
    start +
      maxButtons -
      1
  );

  start = Math.max(
    1,
    end -
      maxButtons +
      1
  );

  items.push(
    <Pagination.Prev
      key="prev"
      disabled={
        loading ||
        page <= 1
      }
      onClick={() =>
        setPage(
          Math.max(
            1,
            page - 1
          )
        )
      }
    />
  );

  for (
    let p = start;
    p <= end;
    p += 1
  ) {
    items.push(
      <Pagination.Item
        key={p}
        active={
          p === page
        }
        disabled={
          loading
        }
        onClick={() =>
          setPage(p)
        }
      >
        {p}
      </Pagination.Item>
    );
  }

  items.push(
    <Pagination.Next
      key="next"
      disabled={
        loading ||
        page >= totalPages
      }
      onClick={() =>
        setPage(
          Math.min(
            totalPages,
            page + 1
          )
        )
      }
    />
  );

  return (
    <Pagination className="mb-0">
      {items}
    </Pagination>
  );
};

const SortableHeader = ({
  column,
  sortConfig,
  onSort,
}) => {
  const isActive =
    sortConfig.key ===
    column.sortKey;

  return (
    <th>
      <button
        type="button"
        className={`report-sort-btn ${
          isActive
            ? "is-active"
            : ""
        }`}
        disabled={
          !column.sortKey
        }
        onClick={() =>
          onSort(
            column.sortKey
          )
        }
      >
        <span>
          {column.label}
        </span>

        {column.sortKey ? (
          isActive ? (
            sortConfig.direction ===
            "asc" ? (
              <ChevronUp
                size={14}
              />
            ) : (
              <ChevronDown
                size={14}
              />
            )
          ) : (
            <ChevronsUp
              size={14}
            />
          )
        ) : null}
      </button>
    </th>
  );
};

const REPORT_TYPE =
  REPORT_TYPES.MONTHLY_TRACKER;

const PanchayathMonthlyTrackerReportPage = () => {
  const dispatch = useDispatch();

  const report = useSelector(
    (state) =>
      state
        .panchayathSubscriptionReports
        ?.monthly_tracker || {
        rows: [],
        meta: {},
      }
  );

  const {
    loading,
    exportLoading,
    error,
  } = useSelector(
    (state) =>
      state
        .panchayathSubscriptionReports ||
      {}
  );

  const rows = report.rows || [];
  const meta = report.meta || {};

  const [searchInput, setSearchInput] =
    useState("");
  const [search, setSearch] =
    useState("");
  const [fyYear, setFyYear] =
    useState(getCurrentFy());
  const [page, setPage] =
    useState(1);
  const [pageSize, setPageSize] =
    useState(20);
  const [sortConfig, setSortConfig] =
    useState({
      key: "pan_name",
      direction: "asc",
    });

  const months =
    Array.isArray(
      meta?.months
    )
      ? meta.months
      : [];

  const columns = [
    {
      key: "sl_no",
      label: "Sl No.",
      sortKey: "pan_id",
    },
    {
      key: "pan_name",
      label: "Panchayath Name",
      sortKey: "pan_name",
    },
    {
      key: "monthly_fee",
      label: "Monthly Fee",
      sortKey: "subscription_amount",
      render: money,
    },
    {
      key: "monthly_fee_with_gst",
      label: "Fee incl. GST",
      sortKey: null,
      render: money,
    },
    {
      key: "onboarding_start_date",
      label: "Onboarding Start Date",
      sortKey: "onboarding_start_date",
      render: dateText,
    },
    {
      key: "bill_start_date",
      label: "Bill Start Date",
      sortKey: "bill_start_date",
      render: dateText,
    },

    ...months.map(
      (month) => ({
        key: month.key,
        label: month.label,
        sortKey: null,
        render: money,
      })
    ),

    {
      key: "total_collected",
      label: "Total Collected",
      sortKey: null,
      render: money,
    },
    {
      key: "pending_approval",
      label: "Pending Approval",
      sortKey: null,
      render: pendingText,
    },
    {
      key: "months_applicable_till_date",
      label: "Months Applicable Till Date",
      sortKey: null,
    },
    {
      key: "expected_till_date",
      label: "Expected Till Date",
      sortKey: null,
      render: moneyOrDash,
    },
    {
      key: "due_amount",
      label: "Due",
      sortKey: null,
      render: dueText,
    },
  ];

  const getFilters = (
    requestPage = page,
    requestPageSize = pageSize
  ) => ({
    fy_year: fyYear,
    page: requestPage,
    pageSize: requestPageSize,
    search,
    sort_field: sortConfig.key,
    sort_direction:
      sortConfig.direction,
  });

  const loadReport = (
    requestPage = page,
    requestPageSize = pageSize
  ) =>
    dispatch(
      fetchPanchayathSubscriptionReportThunk({
        reportType:
          REPORT_TYPE,
        filters:
          getFilters(
            requestPage,
            requestPageSize
          ),
      })
    );

  useEffect(() => {
    loadReport(
      page,
      pageSize
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    search,
    fyYear,
    page,
    pageSize,
    sortConfig.key,
    sortConfig.direction,
  ]);

  useEffect(() => {
    if (!error) return;

    toast.error(
      String(error)
    );

    dispatch(
      clearPanchayathSubscriptionReportError()
    );
  }, [error, dispatch]);

  const handleSort = (key) => {
    if (!key) return;

    setPage(1);

    setSortConfig(
      (prev) => ({
        key,
        direction:
          prev.key === key &&
          prev.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  const submitSearch = (e) => {
    e?.preventDefault();

    setPage(1);
    setSearch(
      searchInput.trim()
    );
  };

  const clearFilters = () => {
    setSearchInput("");
    setSearch("");
    setFyYear(
      getCurrentFy()
    );
    setPage(1);
    setPageSize(20);
    setSortConfig({
      key: "pan_name",
      direction: "asc",
    });
  };

  const handleExport = async (
    format
  ) => {
    try {
      const result =
        await dispatch(
          exportPanchayathSubscriptionReportThunk({
            reportType:
              REPORT_TYPE,
            format,
            filters:
              getFilters(
                1,
                pageSize
              ),
          })
        ).unwrap();

      const suffix =
        `${fyYear}-${String(
          fyYear + 1
        ).slice(-2)}`;

      downloadBlob(
        result.blob,
        `panchayath_monthly_tracker_${suffix}.${
          format === "excel"
            ? "xlsx"
            : "pdf"
        }`
      );
    } catch (e) {
      toast.error(
        String(
          e ||
            "Export failed"
        )
      );
    }
  };

  const totalRecords =
    Number(
      meta?.totalRecords || 0
    );

  const totalPages =
    Math.max(
      1,
      Number(
        meta?.totalPages || 1
      )
    );

  const currentPage =
    Math.max(
      1,
      Number(
        meta?.page || page
      )
    );

  return (
    <div className="report-page">
      <Card className="report-card">
        <Card.Header className="report-card-header">
          <div className="report-header-row">
            <div>
              <div className="report-title">
                Monthly Tracker
              </div>
              <div className="report-subtitle">
                Month-wise approved subscription payments by Panchayath. Pending approvals are shown separately.
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body>
          <div className="report-filter-box">
            <Row className="g-3">
              <Col
                lg={3}
                md={6}
              >
                <Form.Label className="report-label">
                  Financial Year
                </Form.Label>

                <Form.Select
                  value={
                    fyYear
                  }
                  disabled={
                    loading
                  }
                  onChange={(
                    event
                  ) => {
                    setFyYear(
                      Number(
                        event
                          .target
                          .value
                      )
                    );
                    setPage(1);
                  }}
                >
                  {Array.from(
                    {
                      length: 8,
                    },
                    (
                      _,
                      index
                    ) =>
                      getCurrentFy() -
                      4 +
                      index
                  ).map(
                    (year) => (
                      <option
                        key={
                          year
                        }
                        value={
                          year
                        }
                      >
                        {year}-
                        {String(
                          year + 1
                        ).slice(-2)}
                      </option>
                    )
                  )}
                </Form.Select>
              </Col>

              <Col
                lg={5}
                md={6}
              >
                <Form
                  onSubmit={
                    submitSearch
                  }
                >
                  <Form.Label className="report-label">
                    Search
                  </Form.Label>

                  <InputGroup className="report-search">
                    <InputGroup.Text>
                      <Search
                        size={16}
                      />
                    </InputGroup.Text>

                    <Form.Control
                      value={
                        searchInput
                      }
                      disabled={
                        loading
                      }
                      placeholder="Search Panchayath / District / Taluk..."
                      onChange={(
                        event
                      ) =>
                        setSearchInput(
                          event
                            .target
                            .value
                        )
                      }
                    />

                    <Button
                      type="submit"
                      variant="primary"
                    >
                      Search
                    </Button>
                  </InputGroup>
                </Form>
              </Col>

              <Col
                lg={4}
                md={12}
                className="d-flex align-items-end"
              >
                <div className="report-filter-actions">
                  <Button
                    className="report-btn-primary"
                    variant="primary"
                    disabled={
                      loading
                    }
                    onClick={() =>
                      loadReport(
                        1,
                        pageSize
                      )
                    }
                  >
                    {loading ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading
                      </>
                    ) : (
                      "Load"
                    )}
                  </Button>

                  <Button
                    className="report-btn"
                    variant="outline-secondary"
                    disabled={
                      loading
                    }
                    onClick={
                      clearFilters
                    }
                  >
                    <RefreshCcw
                      size={16}
                      className="me-2"
                    />
                    Clear
                  </Button>
                </div>
              </Col>
            </Row>
          </div>

          <div className="report-table-toolbar">
            <div className="report-table-title">
              Monthly Tracker{" "}
              <span className="report-count">
                (
                {totalRecords.toLocaleString(
                  "en-IN"
                )}{" "}
                records)
              </span>
            </div>

            <div className="report-table-right">
              <Button
                className="report-btn"
                variant="outline-primary"
                disabled={
                  loading ||
                  exportLoading ||
                  !rows.length
                }
                onClick={() =>
                  handleExport(
                    "excel"
                  )
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />
                Excel
              </Button>

              <Button
                className="report-btn"
                variant="outline-danger"
                disabled={
                  loading ||
                  exportLoading ||
                  !rows.length
                }
                onClick={() =>
                  handleExport(
                    "pdf"
                  )
                }
              >
                <Download
                  size={16}
                  className="me-2"
                />
                PDF
              </Button>
            </div>
          </div>

          <div className="report-table-wrap">
            <div className="report-table-scroll">
              <Table
                hover
                className="mb-0 report-table"
              >
                <thead>
                  <tr>
                    {columns.map(
                      (
                        column
                      ) => (
                        <SortableHeader
                          key={
                            column.key
                          }
                          column={
                            column
                          }
                          sortConfig={
                            sortConfig
                          }
                          onSort={
                            handleSort
                          }
                        />
                      )
                    )}
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan={
                          columns.length
                        }
                        className="text-center py-4"
                      >
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length ? (
                    rows.map(
                      (
                        row,
                        index
                      ) => (
                        <tr
                          key={
                            row.pan_id ||
                            index
                          }
                        >
                          {columns.map(
                            (
                              column
                            ) => (
                              <td
                                key={
                                  column.key
                                }
                              >
                                {column.render
                                  ? column.render(
                                      row[
                                        column
                                          .key
                                      ]
                                    )
                                  : safe(
                                      row[
                                        column
                                          .key
                                      ]
                                    )}
                              </td>
                            )
                          )}
                        </tr>
                      )
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={
                          columns.length
                        }
                        className="text-center py-4"
                      >
                        No data found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="report-pagination-bar">
              <div className="report-page-size">
                <span>
                  Rows:
                </span>

                <Form.Select
                  value={
                    pageSize
                  }
                  className="page-size-select"
                  disabled={
                    loading
                  }
                  onChange={(
                    event
                  ) => {
                    setPage(1);
                    setPageSize(
                      Number(
                        event
                          .target
                          .value
                      )
                    );
                  }}
                >
                  {[
                    10,
                    20,
                    50,
                    100,
                  ].map(
                    (size) => (
                      <option
                        key={
                          size
                        }
                        value={
                          size
                        }
                      >
                        {size}
                      </option>
                    )
                  )}
                </Form.Select>
              </div>

              <ReportPagination
                loading={
                  loading
                }
                page={
                  currentPage
                }
                totalPages={
                  totalPages
                }
                setPage={
                  setPage
                }
              />

              <div className="report-page-info">
                Page{" "}
                {currentPage} /{" "}
                {totalPages}
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default
PanchayathMonthlyTrackerReportPage;
